import { useRef, useState } from "react";
import { Globe, ImagePlus, Loader2, Lock, Users, Video, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const MAX_IMAGE_MB = 25;
const MAX_VIDEO_MB = 200;
const IMAGE_RE = /^image\/(jpeg|png|webp|gif|heic|heif)$/;
const VIDEO_RE = /^video\/(mp4|quicktime|webm)$/;

/** Downscale/re-encode large photos on-device (max 1920 px, JPEG q0.85). HEIC/GIF pass through for the server. */
export async function compressImage(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size < 600 * 1024) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1920 / Math.max(bmp.width, bmp.height));
    const w = Math.round(bmp.width * scale), h = Math.round(bmp.height * scale);
    const canvas = document.createElement("canvas"); canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch { return file; }
}

export const VISIBILITY = [
  { key: "public", label: "Public", Icon: Globe, hint: "Anyone on PandaX" },
  { key: "followers", label: "Followers", Icon: Users, hint: "People who follow you" },
  { key: "private", label: "Only me", Icon: Lock, hint: "Saved to your wall, hidden from others" },
];

/** Create Post / Add Media — caption + one photo or video + privacy. Mobile-first. */
export default function WallComposer({ open, onOpenChange, onCreated }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [kind, setKind] = useState(null);
  const [visibility, setVisibility] = useState("public");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const input = useRef(null);

  const reset = () => { setText(""); setFile(null); setKind(null); setVisibility("public"); setProgress(0); if (preview) URL.revokeObjectURL(preview); setPreview(null); };

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (input.current) input.current.value = "";
    if (!f) return;
    const t = (f.type || "").toLowerCase();
    const isImg = IMAGE_RE.test(t) || (!t && /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(f.name));
    const isVid = VIDEO_RE.test(t) || (!t && /\.(mp4|mov|webm)$/i.test(f.name));
    if (!isImg && !isVid) return toast.error("Use a photo (JPG, PNG, WEBP, GIF, HEIC) or video (MP4, MOV, WEBM).");
    if (isImg && f.size > MAX_IMAGE_MB * 1024 * 1024) return toast.error(`Photo too large (max ${MAX_IMAGE_MB} MB).`);
    if (isVid && f.size > MAX_VIDEO_MB * 1024 * 1024) return toast.error(`Video too large (max ${MAX_VIDEO_MB} MB).`);
    const ready = isImg ? await compressImage(f) : f;
    setFile(ready); setKind(isImg ? "image" : "video");
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(ready));
  };

  const submit = async () => {
    if (!text.trim() && !file) return toast.error("Add a caption or a photo/video.");
    setBusy(true);
    try {
      let media_url = null;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (ev) => setProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0),
        });
        media_url = data.url;
      }
      const { data: post } = await api.post("/posts", { text: text.trim(), media_url, media_type: kind, visibility });
      toast.success("Posted to your wall.");
      onCreated?.(post); reset(); onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { if (!v) reset(); onOpenChange(v); } }}>
      <DialogContent data-testid="wall-composer" className="bg-[#0c0c0e] border-white/10 text-white max-w-lg p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5"><DialogTitle className="font-display text-xl">Create Post</DialogTitle><DialogDescription className="text-xs text-zinc-500">Share a photo, video or a few words on your wall and choose who can see it.</DialogDescription></DialogHeader>
        <div className="px-5 pb-5 space-y-4">
          <Textarea data-testid="wall-composer-caption" value={text} onChange={(e) => setText(e.target.value.slice(0, 1000))} placeholder="Say something… (optional with media)" rows={3} className="bg-white/5 border-white/10 resize-none text-base" />
          {preview ? (
            <div className="relative rounded-xl overflow-hidden border border-white/10 bg-black">
              {kind === "video"
                ? <video data-testid="wall-composer-video-preview" src={preview} controls playsInline className="w-full max-h-72 object-contain" />
                : <img data-testid="wall-composer-image-preview" src={preview} alt="" className="w-full max-h-72 object-contain" />}
              <button type="button" data-testid="wall-composer-remove-media" onClick={() => { URL.revokeObjectURL(preview); setPreview(null); setFile(null); setKind(null); }} aria-label="Remove media" className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 text-white flex items-center justify-center"><X size={14} /></button>
              <div className="absolute bottom-2 left-2 text-[10px] uppercase tracking-widest bg-black/60 px-2 py-0.5 rounded">{kind} · {(file.size / 1024 / 1024).toFixed(1)} MB</div>
            </div>
          ) : (
            <button type="button" data-testid="wall-composer-add-media" onClick={() => input.current?.click()} className="w-full rounded-xl border border-dashed border-white/15 hover:border-emerald-400/60 py-6 flex flex-col items-center gap-2 text-zinc-400 hover:text-white transition-colors">
              <span className="flex items-center gap-3"><ImagePlus size={20} /><Video size={20} /></span>
              <span className="text-xs uppercase tracking-widest">Add photo or video</span>
              <span className="text-[10px] text-zinc-600">Photos up to {MAX_IMAGE_MB} MB (auto-compressed) · Videos up to {MAX_VIDEO_MB} MB</span>
            </button>
          )}
          <input ref={input} type="file" accept="image/*,video/mp4,video/quicktime,video/webm" onChange={pick} className="hidden" data-testid="wall-composer-file-input" />

          <div className="flex gap-1.5" role="radiogroup" aria-label="Who can see this">
            {VISIBILITY.map(({ key, label, Icon }) => (
              <button key={key} type="button" role="radio" aria-checked={visibility === key} data-testid={`wall-composer-visibility-${key}`} onClick={() => setVisibility(key)} className={`flex-1 h-9 rounded-lg border text-xs inline-flex items-center justify-center gap-1.5 ${visibility === key ? "border-emerald-400 text-emerald-300 bg-emerald-500/10" : "border-white/10 text-zinc-400"}`}><Icon size={12} /> {label}</button>
            ))}
          </div>

          {busy && file && <div className="h-1 bg-white/10 rounded overflow-hidden"><div className="h-full bg-emerald-400 transition-[width]" style={{ width: `${progress}%` }} /></div>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { reset(); onOpenChange(false); }} disabled={busy} className="rounded-xl">Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="wall-composer-submit" className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold min-w-[110px]">{busy ? <Loader2 size={16} className="animate-spin" /> : "Post"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
